import {
  STRIPE_ACCOUNT_QUERY_KEY,
  STRIPE_ONBOARDING_COMPLETE_STATE,
  isStripeAccountPayable,
  stripeAccountNeedsAction,
  useCreateStripeOnboardingLink,
  useFetchSettings,
  useFetchStripeAccount
} from '@/features/wallet/api'
import { routes } from '@/pages/routes'
import { Button, Text } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

type PageState = 'checking' | 'incomplete' | 'reviewing' | 'redirecting' | 'disabled' | 'error'

const REVIEW_POLL_INTERVAL_MS = 5_000
const ACTION_REQUIRED_GRACE_POLLS = 4
const REFRESH_REDIRECT_GUARD_TTL_MS = 60_000

export const StripeReturnPage = () => {
  const { t } = useLingui()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const mode = params.get('mode') === 'refresh' ? 'refresh' : 'return'
  const [state, setState] = useState<PageState>(mode === 'refresh' ? 'redirecting' : 'checking')
  const startedRef = useRef(false)
  const refreshGuardKey = useMemo(() => `dh:stripe-refresh:${window.location.pathname}:${window.location.search}`, [])
  const { data: settings } = useFetchSettings()
  const { data: account, refetch } = useFetchStripeAccount(mode === 'return' && settings?.stripeConnectEnabled === true)
  const { mutateAsync: createOnboardingLink } = useCreateStripeOnboardingLink()

  const redirectToStripe = useCallback(async () => {
    setState('redirecting')
    try {
      const link = await createOnboardingLink()
      window.location.assign(link.url)
    } catch (err) {
      console.warn('Failed to create Stripe onboarding link', err)
      setState('error')
      toast.error(t`Couldn't continue Stripe onboarding — try again.`)
    }
  }, [createOnboardingLink, refreshGuardKey, t])

  useEffect(() => {
    if (startedRef.current) return
    if (!settings) return
    if (!settings.stripeConnectEnabled) {
      setState('disabled')
      startedRef.current = true
      return
    }
    startedRef.current = true
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    if (mode === 'refresh') {
      const lastRedirectAt = Number(sessionStorage.getItem(refreshGuardKey) ?? 0)
      if (lastRedirectAt > 0 && Date.now() - lastRedirectAt < REFRESH_REDIRECT_GUARD_TTL_MS) {
        setState('error')
        return undefined
      }
      sessionStorage.setItem(refreshGuardKey, String(Date.now()))
      void redirectToStripe()
      return undefined
    }

    void queryClient.invalidateQueries({ queryKey: STRIPE_ACCOUNT_QUERY_KEY })
    const scheduleNextCheck = (attempt: number) => {
      retryTimer = setTimeout(() => void checkAccount(attempt + 1), REVIEW_POLL_INTERVAL_MS)
    }

    const checkAccount = async (attempt: number) => {
      const result = await refetch()
      if (cancelled) return
      if (result.isError) {
        if (attempt === 0) {
          setState('error')
          toast.error(t`Couldn't check Stripe account — try again.`)
          return
        }
        scheduleNextCheck(attempt)
        return
      }
      if (isStripeAccountPayable(result.data)) {
        setState('redirecting')
        navigate(routes.wallet.path, {
          replace: true,
          state: { [STRIPE_ONBOARDING_COMPLETE_STATE]: true }
        })
        return
      }
      if (result.data) {
        const actionRequired = stripeAccountNeedsAction(result.data)
        if (actionRequired && attempt >= ACTION_REQUIRED_GRACE_POLLS) {
          setState('incomplete')
          return
        }
        setState('reviewing')
        scheduleNextCheck(attempt)
        return
      }
      setState('error')
    }
    void checkAccount(0)

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [mode, navigate, queryClient, redirectToStripe, refetch, refreshGuardKey, settings, t])

  const backToWallet = useCallback(() => {
    navigate(routes.wallet.path)
  }, [navigate])

  const retryOnboarding = useCallback(() => {
    void redirectToStripe()
  }, [redirectToStripe])

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4">
      {(state === 'checking' || state === 'redirecting') && (
        <>
          <Text className="mb-4 text-center" type="h3">
            {state === 'redirecting' ? (
              <Trans>Continuing Stripe onboarding…</Trans>
            ) : (
              <Trans>Checking Stripe account…</Trans>
            )}
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>Please wait — don&apos;t close this tab.</Trans>
          </Text>
        </>
      )}

      {state === 'reviewing' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Stripe is reviewing your account.</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>
              You can return to your wallet now. We'll enable Stripe withdrawals after Stripe approves your account.
            </Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>If Stripe needs more information, this page will update automatically.</Trans>
          </Text>
          <Button className="mt-4" onClick={backToWallet}>
            <span className="colorWhite">
              <Trans>Back to wallet</Trans>
            </span>
          </Button>
        </>
      )}

      {state === 'incomplete' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Stripe onboarding is incomplete.</Trans>
          </Text>
          {account?.requirementsCurrentlyDue?.length ? (
            <Text className="colorWhiteToBlack my-2 text-center">
              <Trans>Stripe still needs more information before payouts can be sent.</Trans>
            </Text>
          ) : null}
          <Button className="mt-4" onClick={retryOnboarding}>
            <span className="colorWhite">
              <Trans>Continue with Stripe</Trans>
            </span>
          </Button>
          <Button className="mt-2" variant="tertiary" onClick={backToWallet}>
            <Trans>Back to wallet</Trans>
          </Button>
        </>
      )}

      {state === 'error' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Couldn&apos;t continue Stripe onboarding.</Trans>
          </Text>
          <Button className="mt-4" onClick={retryOnboarding}>
            <span className="colorWhite">
              <Trans>Try again</Trans>
            </span>
          </Button>
          <Button className="mt-2" variant="tertiary" onClick={backToWallet}>
            <Trans>Back to wallet</Trans>
          </Button>
        </>
      )}

      {state === 'disabled' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Stripe payouts are not available.</Trans>
          </Text>
          <Button className="mt-4" onClick={backToWallet}>
            <span className="colorWhite">
              <Trans>Back to wallet</Trans>
            </span>
          </Button>
        </>
      )}
    </div>
  )
}

export default StripeReturnPage
